/*!

=========================================================
* Argon Dashboard React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from 'react';

// reactstrap components
import {
  Spinner,
  Badge,
  Button,
  Card,
  CardHeader,
  Table,
  Row,
  Alert,
  Container,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from 'reactstrap';

import utils from '../utils.js';

class ProductsTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ...this.getIntentDefaults(),
      ...this.getProductDefaults(),
    };
    this.isProductTracked = this.isProductTracked.bind(this);
    this.handleIntentStatusToggleChange = this.handleIntentStatusToggleChange.bind(
      this
    );
    this.handleMaxModeToggleChange = this.handleMaxModeToggleChange.bind(this);
    this.updateIntentMap = this.updateIntentMap.bind(this);
    this.updateProducts = this.updateProducts.bind(this);
    this.toggleProductIntent = this.toggleProductIntent.bind(this);
    this.saveTrackedProducts = this.saveTrackedProducts.bind(this);
  }

  getProductDefaults() {
    return {
      products: null,
      productsLoading: false,
    };
  }

  getIntentDefaults() {
    return {
      intentMap: null,
      intentMapLoading: false,
      intentsSaving: false,
      intentsTouched: false,
      dropdownOpen: false,
      netChange: 0,
    };
  }

  async componentDidMount() {
    await Promise.all([this.updateProducts(), this.updateIntentMap()]);
  }

  async componentDidUpdate(prevProps) {
    if (this.props.didSelectedCredChange(this.props, prevProps)) {
      await Promise.all([this.updateProducts(), this.updateIntentMap()]);
    }
  }

  async updateProducts() {
    if (
      !this.props.selectedAccount ||
      !this.props.selectedAccount.vendorAccountId
    ) {
      this.setState(this.getProductDefaults());
      return;
    }

    this.setState({
      ...this.getProductDefaults(),
      productsLoading: true,
    });

    const vendorAccountId = this.props.selectedAccount.vendorAccountId;
    const { products, errorMessage } = await utils.getProducts(vendorAccountId);
    this.setState({
      products,
      productsLoading: false,
      errorMessage,
    });
  }

  async updateIntentMap() {
    if (
      !this.props.selectedAccount ||
      !this.props.selectedAccount.vendorAccountId
    ) {
      this.setState(this.getIntentDefaults());
      return;
    }
    this.setState({
      ...this.getIntentDefaults(),
      intentMapLoading: true,
    });

    const vendorAccountId = this.props.selectedAccount.vendorAccountId;
    const { intentMap, errorMessage } = await utils.getTrackedProducts(
      vendorAccountId
    );
    this.setState({
      intentMap,
      intentMapLoading: false,
      errorMessage,
    });
  }

  toggleProductIntent(product, toggleProperty) {
    if (!this.state.intentMap) return;
    const intentMapCopy = JSON.parse(JSON.stringify(this.state.intentMap));
    const matchingIntent = intentMapCopy[product.productId];

    let change = 0;

    if (toggleProperty === 'intentStatus') {
      if (matchingIntent) {
        let newStatus;
        if (matchingIntent.intentStatus === 'active') {
          newStatus = 'inactive';
          change--;
        } else {
          newStatus = 'active';
          change++;
        }
        matchingIntent.intentStatus = newStatus;
      } else {
        intentMapCopy[product.productId] = {
          productId: product.productId,
          intentStatus: 'active',
        };
        change++;
      }
    } else if (toggleProperty === 'isMaxMode') {
      if (matchingIntent) {
        matchingIntent.isMaxMode = !matchingIntent.isMaxMode;
        // Also set intentStatus active if turning on max mode
        if (
          matchingIntent.isMaxMode &&
          matchingIntent.intentStatus !== 'active'
        ) {
          matchingIntent.intentStatus = 'active';
        }
      } else {
        intentMapCopy[product.productId] = {
          productId: product.productId,
          intentStatus: 'active',
          isMaxMode: true,
        };
      }
    } else {
      return;
    }

    this.setState({
      intentMap: intentMapCopy,
      intentsTouched: true,
      netChange: this.state.netChange + change,
    });
  }

  async saveTrackedProducts(intentMap, setToIntentMap) {
    if (!intentMap || !this.props.selectedAccount) return;

    this.setState({
      intentsSaving: true,
    });

    const { success, errorMessage } = await utils.putTrackedProducts(
      this.props.selectedAccount.vendorAccountId,
      intentMap,
      setToIntentMap
    );

    if (success) {
      this.setState({
        intentsTouched: false,
        intentsSaving: false,
        netChange: 0,
      });
    } else {
      this.setState({
        errorMessage,
        intentsSaving: false,
      });
    }
  }

  getLines(products) {
    if (!products) return null;
    const linesSet = new Set();
    const vendor = this.props.selectedCred.vendor;
    const vaType = this.props.selectedCred.vaType;

    for (const p of products) {
      if (
        !p.vendorProducts ||
        p.vendorProducts.length === 0 ||
        !p.vendorProducts.find((vp) => vp.vendor === vendor)
      ) {
        continue;
      }
      linesSet.add(p.line);
    }

    let lines = [...linesSet];
    lines = lines.sort();
    lines.unshift('ENABLED');
    if (vaType === 'lite') {
      lines.unshift('LITE');
    } else {
      lines.unshift('ALL');
    }
    return lines;
  }

  renderErrors() {
    if (this.state.errorMessage) {
      return (
        <Alert color="danger" className="mt-2">
          <strong>Error</strong> {this.state.errorMessage}
        </Alert>
      );
    }
  }

  handleIntentStatusToggleChange(product) {
    this.toggleProductIntent(product, 'intentStatus');
  }

  handleMaxModeToggleChange(product) {
    // TODO: If intentStatus is inactive and new max mode is on, set intentStatus active
    this.toggleProductIntent(product, 'isMaxMode');
  }

  renderSaveButton = () => {
    if (!this.state.intentMap) return null;
    const color = this.state.intentsTouched ? 'warning' : 'primary';
    const saveButton = (
      <Button
        disabled={!this.state.intentsTouched}
        color={color}
        size="sm"
        outline
        type="button"
        className="ml-2"
        onClick={() => this.saveTrackedProducts(this.state.intentMap)}
      >
        Save Changes
      </Button>
    );
    return saveButton;
  };

  static get spinner() {
    return <Spinner className="ml-2 mb-1" size="sm" color="warning" />;
  }

  renderSpinner() {
    if (
      !this.state.productsLoading &&
      !this.state.intentsSaving &&
      !this.state.intentMapLoading
    )
      return null;
    let msg = null;
    if (this.state.productsLoading) {
      msg = 'Loading products...';
    } else if (this.state.intentMapLoading) {
      msg = 'Loading your enabled products...';
    } else if (this.state.intentsSaving) {
      msg = 'Saving...';
    }
    return (
      <>
        {ProductsTable.spinner}
        <span className="ml-2 small text-gray">{msg}</span>
      </>
    );
  }

  shouldShowTable() {
    return (
      !this.isTableLoading() && this.state.intentMap && this.state.products
    );
  }

  isTableLoading() {
    return this.state.productsLoading || this.state.intentMapLoading;
  }

  isProductTracked(product) {
    if (!this.state.intentMap) return false;
    const intent = this.state.intentMap[product.productId];
    return intent && intent.intentStatus === 'active';
  }

  isProductMaxModeOn(product) {
    if (!this.state.intentMap) return false;
    const intent = this.state.intentMap[product.productId];
    return intent && intent.isMaxMode;
  }

  renderVendorProducts(vendorProducts, vendor) {
    if (!vendorProducts || vendorProducts.length === 0) return 'Coming Soon™';
    let vpNum = 0;
    return vendorProducts
      .filter((vp) => {
        if (vendor !== vp.vendor) return false;
        if (this.state.lineFilter === 'LITE' && vp.vendor !== 'amazon')
          return false;
        return true;
      })
      .map((vp) => {
        vpNum++;
        const key = `vp-${vpNum}`;
        return (
          <a className="text-white" href={vp.link} key={key} target="_blank">
            {vp.vendorProductId ? (
              <u>{vp.vendorProductId}</u>
            ) : (
              <div>
                <span>
                  <span className="text-muted">HIDDEN</span>{' '}
                  <span
                    id={key}
                    className="text-white text-underline"
                    // For some reason the tailwind cursor-pointer class isn't working, so put the style directly
                    style={{ cursor: 'pointer' }}
                  >
                    [why?]
                  </span>
                </span>
                <UncontrolledTooltip delay={0} target={key}>
                  New SKUs are hidden until some time after launch for security
                  reasons.
                </UncontrolledTooltip>
              </div>
            )}
          </a>
        );
      })
      .reduce((acc, x) => (acc === null ? [x] : [acc, ' & ', x]), null);
  }

  renderFilters() {
    if (!this.shouldShowTable()) return null;
    const lines = this.getLines(this.state.products);
    if (!lines || lines.length < 1) return null;

    if (!this.state.lineFilter || !lines.includes(this.state.lineFilter)) {
      this.state.lineFilter = lines[0];
    }

    return (
      <div className="mb-2 mr-3">
        {lines.map((l) => (
          <Button
            key={l}
            color={this.state.lineFilter === l ? 'primary' : 'default'}
            size="sm"
            type="button"
            className="mr-2 mt-3"
            onClick={() => this.setState({ lineFilter: l })}
          >
            {l}
          </Button>
        ))}
      </div>
    );
  }

  /**
   * Makes a JSX element representing the resale price for the given `productId`.
   * @param {string} productId The product ID.
   * @returns {JSX.Element|undefined}
   */
  makeResalePriceElement(productId) {
    if (!this.props.trends) return;

    const trend = this.props.trends.find((p) => p.productId === productId);
    if (!trend) return;

    const resalePrice = trend.resalePrice;
    if (!resalePrice) return;

    const sampledListingsCount = trend.sampledListingsCount;
    if (!sampledListingsCount) return;

    const sampledListingsEndedDatesRangeFormatted =
      trend.sampledListingsEndedDatesRangeFormatted;
    if (!sampledListingsEndedDatesRangeFormatted) return;

    const resalePriceText = `$${resalePrice}`;
    const resalePriceStyle = {
      textDecorationLine: 'underline',
      textDecorationStyle: 'dotted',
    };

    const infoTooltipId = productId + '-resale-price';
    const infoTooltipPrefix =
      sampledListingsCount === 1 ? 'Sold price' : 'Avg sold price';

    const listingsCountFormatted = sampledListingsCount.toLocaleString();
    const listingsNounFormatted = utils.maybePluralize(
      sampledListingsCount,
      'listing'
    );
    const listingsDateRangeStyle = { whiteSpace: 'nowrap' };

    return (
      <>
        <span id={infoTooltipId} style={resalePriceStyle}>
          {resalePriceText}
        </span>
        <UncontrolledTooltip delay={0} target={infoTooltipId}>
          {infoTooltipPrefix} of <strong>{listingsCountFormatted}</strong>{' '}
          {listingsNounFormatted}{' '}
          <span style={listingsDateRangeStyle}>
            {sampledListingsEndedDatesRangeFormatted}
          </span>
        </UncontrolledTooltip>
      </>
    );
  }

  /**
   * Attempts to return a JSX element representing the resale price for the given `productId`.
   * If products or trends are still loading, it returns a spinner.
   * If there is a failure, it returns an element with N/A text.
   * @param {string} productId The product ID.
   * @returns {JSX.Element}
   */
  renderResalePrice(productId) {
    if (this.state.productsLoading || this.props.trendsLoading) {
      return ProductsTable.spinner;
    }

    const element = this.makeResalePriceElement(productId);

    if (!element) {
      return <span className="text-muted">N/A</span>;
    }

    return element;
  }

  /**
   * Makes an up or down arrow based on the sign of the given `percentage`.
   * @param {number} percentage The percentage value.
   * @returns {JSX.Element}
   */
  makeResalePriceDeltaIcon(percentage) {
    const colorClass = percentage > 0 ? 'text-success' : 'text-danger';

    const iconName = percentage > 0 ? 'chevron-up' : 'chevron-down';
    const iconBrightness = '0.8';
    const iconStyle = {
      filter: `brightness(${iconBrightness})`,
    };

    return (
      <i
        style={iconStyle}
        className={`fas fa-fw fa-sm fa-${iconName} ${colorClass}`}
      />
    );
  }

  /**
   * Makes a JSX element representing the resale price delta for the given `productId`.
   * @param {string} productId The product ID.
   * @returns {JSX.Element|undefined}
   */
  makeResalePriceDeltaElement(productId) {
    if (!this.props.trends) return;

    const trend = this.props.trends.find((p) => p.productId === productId);
    if (!trend) return;

    const percentageChange = trend.resalePricePercentageChangeFromLastWeek;
    if (!percentageChange || percentageChange === 0) return;

    const priceChange = trend.resalePriceChangeFromLastWeek;
    if (!priceChange || priceChange === 0) return;

    const icon = this.makeResalePriceDeltaIcon(percentageChange);
    const percentageText = `${Math.abs(percentageChange)}%`;
    const percentageStyle = {
      textDecorationLine: 'underline',
      textDecorationStyle: 'dotted',
    };

    const infoTooltipId = productId + '-resale-price-delta';
    const priceChangeVerb = priceChange > 0 ? 'increased' : 'decreased';

    return (
      <>
        <span>
          {icon}{' '}
          <span id={infoTooltipId} style={percentageStyle}>
            {percentageText}
          </span>
          <UncontrolledTooltip delay={0} target={infoTooltipId}>
            Avg sold price {priceChangeVerb} by{' '}
            <strong>${Math.abs(priceChange)}</strong> since last week
          </UncontrolledTooltip>
        </span>
      </>
    );
  }

  /**
   * Attempts to return a JSX element representing the resale price delta for the given `productId`.
   * If products or trends are still loading, it returns a spinner.
   * If there is a failure, it returns an element with an em dash for its text.
   * @param {string} productId The product ID.
   * @returns {JSX.Element}
   */
  renderResalePriceDelta(productId) {
    if (this.state.productsLoading || this.props.trendsLoading) {
      return ProductsTable.spinner;
    }

    const element = this.makeResalePriceDeltaElement(productId);

    if (!element) {
      return <span className="text-muted">—</span>;
    }

    return element;
  }

  renderRows() {
    if (!this.state.products) return;
    const vaType = this.props.selectedCred.vaType;

    let products = this.state.products.sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    if (this.state.lineFilter === 'ALL') {
      // no-op
    } else if (this.state.lineFilter === 'ENABLED') {
      products = products.filter((p) => {
        const intent = this.state.intentMap[p.productId];
        return intent && intent.intentStatus === 'active';
      });
    } else if (this.state.lineFilter === 'LITE') {
      products = products.filter(
        (p) =>
          p.vendorProducts && p.vendorProducts.find((vp) => vp.isLiteEligible)
      );
    } else {
      products = products.filter((p) => this.state.lineFilter === p.line);
    }
    let count = 0;

    const vendor = this.props.selectedCred.vendor;
    products = products.filter(
      (p) =>
        p.vendorProducts.length === 0 ||
        p.forceShow ||
        p.vendorProducts.find((vp) => vp.vendor === vendor)
    );

    return products.map((p) => {
      let isEnabled = true;
      const hasMaxMode = p.vendorProducts.find((vp) => vp.hasMaxMode);

      if (vaType === 'lite') {
        if (
          !p.vendorProducts ||
          !p.vendorProducts.find((vp) => vp.isLiteEligible)
        ) {
          isEnabled = false;
        }
      }

      count += 1;

      return (
        <tr key={p.productId}>
          <td>
            {isEnabled ? (
              <span>
                <label
                  className="custom-toggle custom-toggle-green-full"
                  style={{ marginBottom: '0px', marginTop: '6px' }}
                >
                  <input
                    checked={this.isProductTracked(p) ? 1 : 0}
                    onChange={() => this.handleIntentStatusToggleChange(p)}
                    type="checkbox"
                  />
                  <span className="custom-toggle-slider rounded-circle" />
                </label>
              </span>
            ) : (
              <div
                className="custom-toggle"
                style={{ marginBottom: '0px', marginTop: '6px' }}
              >
                {this.renderProOnlyText()}
              </div>
            )}
          </td>

          <td>
            <div className="d-flex align-items-center">
              <span
                className="mr-2"
                style={{ minWidth: '30px', textAlign: 'center' }}
              >
                {this.isProductMaxModeOn(p)
                  ? 'MAX'
                  : this.getMaxOrderQtyFromVP(p.vendorProducts) || '1'}
              </span>
              {hasMaxMode ? (
                <span>
                  <label
                    className="custom-toggle custom-toggle-orange-full"
                    style={{ marginBottom: '0px', marginTop: '6px' }}
                  >
                    <input
                      checked={this.isProductMaxModeOn(p) ? 1 : 0}
                      onChange={() => this.handleMaxModeToggleChange(p)}
                      type="checkbox"
                    />
                    <span
                      className="custom-toggle-slider rounded-circle"
                      style={{
                        borderColor: '#FFD600',
                      }}
                    />
                  </label>
                </span>
              ) : null}
            </div>
          </td>

          <td scope="row">{p.name}</td>

          <td>{this.renderVendorProducts(p.vendorProducts, vendor)}</td>

          <td>{p.productId}</td>
        </tr>
      );
    });
  }

  renderProOnlyText() {
    if (
      this.props.license &&
      this.props.license.isProEligible &&
      this.props.license.licenseType === 'lite' &&
      this.props.user &&
      !utils.isProUpgradeExpired(this.props.user.notifyProUpgradeISOTime)
    ) {
      return (
        <a href="/dashboard/subscribe" className="text-success">
          Go Pro ➔
        </a>
      );
    }
    return <span className="text-muted">Pro only</span>;
  }

  getMaxOrderQtyFromVP(vps) {
    const vendor = this.props.getSelectedVendor();
    const vp = vps
      .filter((vp) => vp.vendor === vendor)
      .find((vp) => vp.maxOrderQty);
    if (!vp) return;
    return vp.maxOrderQty;
  }

  toggle = () => {
    this.setState({
      dropdownOpen: !this.state.dropdownOpen,
    });
  };

  handleCopyIntents = async (account) => {
    if (!account) return;
    this.setState({
      ...this.getIntentDefaults(),
      intentsSaving: true,
    });

    const { intentMap, errorMessage } = await utils.getTrackedProducts(
      account.vendorAccountId
    );

    if (!intentMap) return;

    await this.saveTrackedProducts(intentMap, true);

    this.setState({
      intentsTouched: false,
      intentMap,
      intentsSaving: false,
      intentMapLoading: false,
      errorMessage,
    });
  };

  handleUnselectAll = () => {
    if (!this.state.intentMap) return;
    const intentMapCopy = JSON.parse(JSON.stringify(this.state.intentMap));

    let numUnselected = 0;
    for (const i of Object.values(intentMapCopy)) {
      if (!i) continue;
      if (i.intentStatus === 'active') numUnselected++;
      i.intentStatus = 'inactive';
    }
    const newState = {
      intentMap: intentMapCopy,
    };
    if (!this.state.intentsTouched && numUnselected > 0) {
      newState.intentsTouched = true;
    }
    this.setState(newState);
  };

  renderUnselectAll() {
    if (!this.shouldShowTable()) return null;
    return (
      <Button
        className="btn-icon btn-3 float-right"
        type="button"
        color="primary"
        size="sm"
        outline
        onClick={this.handleUnselectAll}
      >
        <span className="btn-inner--icon">
          <i className="ni ni-active-40" />
        </span>
        <span className="">Unselect all</span>
      </Button>
    );
  }

  renderCredBadge(cred) {
    return (
      <Badge color="success" className="mr-2">
        {cred.vendor} {cred.slotNum}
      </Badge>
    );
  }

  renderCopyProducts() {
    if (!this.shouldShowTable()) return null;
    let currCredentialNum = 0;
    const selectedCred = this.props.selectedCred;
    const selectedAccount = this.props.selectedAccount;
    let credentials = this.props.credentials || [];
    credentials = credentials.filter(
      (c) =>
        c.account &&
        c.account !== selectedAccount &&
        selectedCred.vendor === c.vendor &&
        selectedCred.vaType === c.vaType
    );

    return (
      <Dropdown
        isOpen={this.state.dropdownOpen}
        toggle={this.toggle}
        className="float-right"
      >
        <DropdownToggle caret outline color="primary" size="sm">
          <span className="btn-inner--icon">
            <i className="ni ni-single-copy-04" />
          </span>
          <span className="ml-1">Copy products from other account</span>
        </DropdownToggle>
        <DropdownMenu>
          {credentials.length === 0 ? (
            <DropdownItem disabled>{`No other ${utils.titleCase(
              selectedCred.vendor
            )} accounts :(`}</DropdownItem>
          ) : (
            credentials.map((c) => {
              const account = c.account;
              currCredentialNum++;
              return (
                <span key={`${currCredentialNum}`}>
                  {/*<DropdownItem header className="text-black-50">*/}
                  {/*  {`${c.vendor} Instance ${c.slotNum}`}*/}
                  {/*</DropdownItem>*/}
                  <DropdownItem
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.handleCopyIntents(account)}
                  >
                    {utils.renderCredBadge(c)}
                    {account ? (
                      <b className="font-weight-600">{account.email}</b>
                    ) : (
                      <span className="font-weight-600 text-orange">
                        No account
                      </span>
                    )}
                    {/*<span className="small ml-2">*/}
                    {/*  ({this.getTrackedFraction()})*/}
                    {/*</span>*/}
                  </DropdownItem>
                  {currCredentialNum < credentials.length ? (
                    <DropdownItem divider />
                  ) : null}
                </span>
              );
            })
          )}
        </DropdownMenu>
      </Dropdown>
    );
  }

  getTrackedFraction() {
    const vendor = this.props.selectedCred.vendor;
    let productCount = 0;
    for (const p of this.state.products) {
      if (
        p.forceShow ||
        (p.vendorProducts &&
          p.vendorProducts.find((vp) => vp.vendor === vendor))
      ) {
        productCount++;
      }
    }

    let intentCount = 0;
    for (const [key, intent] of Object.entries(this.state.intentMap)) {
      if (intent && intent.intentStatus === 'active') intentCount++;
    }
    return `${intentCount}/${productCount}`;
  }

  renderProductCount() {
    if (this.isTableLoading()) return null;
    if (!this.state.products) return null;
    if (!this.state.intentMap) return null;

    let suffix = 'products enabled';
    if (this.state.intentsTouched) {
      // if (this.state.netChange) {
      //   let changeStr =
      //     this.state.netChange > 0
      //       ? `+${this.state.netChange}`
      //       : this.state.netChange;
      //   suffix += ` (${changeStr} unsaved)`;
      // } else {
      suffix += ' (unsaved)';
      // }
    }

    return (
      <span className="text-white mt-1 mr-3 float-right small">
        {this.getTrackedFraction()} {suffix}
      </span>
    );
  }

  renderNote() {
    if (!this.shouldShowTable()) return null;
    return (
      <div className="text-gray">
        Tip: Want us to add an item? Submit a request in the{' '}
        <a
          // href="https://discord.com/channels/797273140564197386/839085800343076915"
          href="https://discord.gg/nm6RJxN8fr"
          target="_blank"
        >
          #product-requests
        </a>{' '}
        Discord channel.
      </div>
    );
  }

  checkIfVendorHasMaxMode() {
    if (!this.state.products) return false;
    const vendor = this.props.selectedCred.vendor;
    return this.state.products.find((p) =>
      p.vendorProducts.find((vp) => vp.vendor === vendor && vp.hasMaxMode)
    );
  }

  renderTable() {
    if (!this.shouldShowTable()) return null;

    return (
      <Table
        className="align-items-center table-dark table-flush table-striped overflow-hidden"
        responsive
      >
        <thead className="thead-dark">
          <tr>
            <th scope="col">Autobuy</th>

            <th scope="col">
              <span id="col-qty-per-order">
                <span className="ml-2">Qty</span>
                {/* <span>Qty</span> <span className="text-green">[?]</span> */}
              </span>
              {/* <UncontrolledTooltip delay={0} target="col-qty-per-order">
                  Item quantity for a single order.
                </UncontrolledTooltip> */}
            </th>

            <th scope="col">Product</th>
            <th scope="col">SKU</th>
            <th scope="col">Snail ID</th>
          </tr>
        </thead>
        <tbody>{this.renderRows()}</tbody>
      </Table>
    );
  }

  render() {
    return (
      <>
        <Container className="mt--7" fluid>
          {/* Dark table */}
          <Row>
            <div className="col">
              <Card className="bg-default shadow">
                <CardHeader className="bg-default border-0 sticky z-999">
                  <h3 className="text-white mb-0">
                    Products
                    {this.renderSaveButton()}
                    {this.renderSpinner()}
                    {this.renderCopyProducts()}
                    {this.renderUnselectAll()}
                    {this.renderProductCount()}
                    {this.renderFilters()}
                  </h3>
                  {this.renderErrors()}
                  {this.renderNote()}
                </CardHeader>
                {this.renderTable()}
              </Card>
            </div>
          </Row>
        </Container>
      </>
    );
  }
}

export default ProductsTable;
